<script setup lang="ts">
import type { CmsElementHeadlineTextBackground } from "~/composables/useCustom";
import { getTranslatedProperty } from "@shopware-pwa/helpers-next";
import { useCategory } from "#imports";
import innerBannerBg from "~/assets/images/inner-banner-bg.svg";
const { category } = useCategory();

const props = defineProps<{
  content: CmsElementHeadlineTextBackground;
}>();

const { headlineText, mainText, bgImage } = props.content.data;

const backgroundStyle = computed(() => {
  if (bgImage?.media?.url) {
    return {
      backgroundImage: `url('${bgImage.media.url}')`,
    };
  }
  return {
    backgroundImage: `url('${innerBannerBg}')`,
  };
});

const extractPdfUrl = (text: string): string | null => {
  // Regex patterns to find PDF URLs in different contexts
  const urlPatterns = [
    // PDF URLs within href attributes
    /href=["']([^"']*\.pdf(?:\?[^"']*)?)/i,

    // Direct PDF URLs in text (http or https)
    /https?:\/\/[^\s"']+\.pdf(?:\?[^\s"']*)?/i,

    // PDF URLs that might be partially obscured or have additional parameters
    /(?:https?:\/\/)?[^\s"']+\.pdf(?:\?[^\s"']*)?/i,
  ];

  // Try each pattern
  for (const pattern of urlPatterns) {
    const match = text.match(pattern);
    if (match) {
      // Get the first captured group or the entire match
      const url = match[1] || match[0];

      // Basic validation and absolute URL handling
      if (url) {
        // If it's a relative URL, you might want to prepend a base domain
        return url.startsWith("http")
          ? url
          : ``;
      }
    }
  }

  return null;
};
const pdfUrl = ref<string | null>(null);
onMounted(async () => {
  if (mainText) {
    const foundPdfUrl = extractPdfUrl(mainText);
    if (foundPdfUrl) {
      pdfUrl.value = foundPdfUrl;
    }
  }
});
</script>
<template>
  <div
    class="inner-banner relative bg-c-light-grey bg-cover bg-center w-full px-4"
    :style="backgroundStyle"
  >
    <div class="absolute inset-0 bg-light-grey opacity-30"></div>
    <div class="container mx-auto relative z-[2px] py-8 md:py-10">
      <div class="title text-left md:text-center">
        <h1
          class="text-2xl md:text-43px font-bold text-dark-bg mb-3 md:mb-4 leading-none"
        >
          <template v-if="headlineText">
            {{ headlineText }}
          </template>
          <template v-else-if="getTranslatedProperty(category, 'name')">
            {{ getTranslatedProperty(category, "name") }}
          </template>
        </h1>
        <div v-if="mainText" class="text-sm text-dark-bg" v-html="mainText" />
        <p v-else class="text-sm text-dark-bg">
          {{ getTranslatedProperty(category, "description") }}
        </p>
        <iframe
          v-if="pdfUrl"
          class="mt-4"
          :src="pdfUrl"
          frameborder="0"
          width="100%"
          height="850px"
        ></iframe>
      </div>
    </div>
  </div>
</template>
<style scoped>
::v-deep(a) {
  text-decoration: underline !important;
}
::v-deep(a:hover) {
  color: #f87171 !important;
}
</style>
